import { useCallback, useRef, useState } from "react";
import * as pmRepo from "@/stores/pm/repository";
import type { PMIssueComment } from "@/domains/pm/types";

export type ProtocolCommentsStatus = "idle" | "loading" | "ready" | "error";

/** Stable identity, so an idle or failed state never re-renders on a new Set. */
const NO_INCOMPLETE: ReadonlySet<number> = new Set<number>();

export interface ProtocolCommentsState {
  status: ProtocolCommentsStatus;
  comments: Map<number, PMIssueComment[]> | null;
  /** The scan hit its cap, so what printed is a slice of the period. */
  truncated: boolean;
  /** The issues that slice cut short — their comment counts are unknown. */
  incomplete: ReadonlySet<number>;
  /** Which period `comments` belongs to — the key `useProtocolData` uses. */
  key: string | null;
}

interface CacheEntry {
  comments: Map<number, PMIssueComment[]>;
  truncated: boolean;
  incomplete: Set<number>;
  /** The issues this entry was fetched FOR, and their sorted identity. */
  ids: Set<number>;
  signature: string;
}

const IDLE: ProtocolCommentsState = {
  status: "idle",
  comments: null,
  truncated: false,
  incomplete: NO_INCOMPLETE,
  key: null,
};

const signatureOf = (ids: number[]): string => [...ids].sort((a, b) => a - b).join(",");

/**
 * Whether a cached entry answers this request.
 *
 * A truncated entry is reused only for the very request that produced it: it is
 * a slice, so a different selection deserves its own fetch, while the same one
 * must not fetch forever.
 */
function covers(
  entry: CacheEntry | undefined,
  signature: string,
  issueIds: number[]
): entry is CacheEntry {
  if (!entry) return false;
  if (entry.signature === signature) return true;
  return !entry.truncated && issueIds.every((id) => entry.ids.has(id));
}

/** The ready state a cache hit produces, reusing `prev` when it already says
 *  exactly this — an unchanged entry must not re-render on a new object. */
function readyFromCache(
  prev: ProtocolCommentsState,
  entry: CacheEntry,
  key: string
): ProtocolCommentsState {
  if (prev.status === "ready" && prev.key === key && prev.comments === entry.comments) return prev;
  return {
    status: "ready",
    comments: entry.comments,
    truncated: entry.truncated,
    incomplete: entry.incomplete,
    key,
  };
}

/**
 * The period's comments, fetched only once somebody asks to print them.
 *
 * Deliberately a second fetch rather than part of `getProtocolData`. Comments
 * are payload, never membership — a comment already writes an activity row, so
 * the period's own query has settled which issues belong long before this runs.
 * The column also starts off, so folding this into the period fetch would tax
 * every export for a feature most never use, and would turn the repository
 * test's "the period fetch never touches pm_issue_comments" guard from a true
 * statement about the design into one more deleted assertion.
 *
 * Fetched for the TICKED issues, because the 5000-comment cap is spent in
 * `(issue_id, id)` order: asking for the whole period lets issues nobody is
 * exporting eat the budget and cut the ones that are. The cached result is
 * still reused whenever it covers the request — the first fetch is the whole
 * period, so unticking and re-ticking stays free, and only a fetch that
 * actually hit the cap is re-run against the narrower selection.
 */
export function useProtocolComments() {
  const [state, setState] = useState<ProtocolCommentsState>(IDLE);
  const cacheRef = useRef(new Map<string, CacheEntry>());
  const inFlightRef = useRef<{ signature: string; controller: AbortController } | null>(null);

  const abort = useCallback(() => {
    inFlightRef.current?.controller.abort();
    inFlightRef.current = null;
  }, []);

  const load = useCallback(
    (key: string, issueIds: number[], bounds: { since: string; until: string }) => {
      const signature = signatureOf(issueIds);
      const cached = cacheRef.current.get(key);
      if (covers(cached, signature, issueIds)) {
        // A fetch still running for another selection would otherwise pass the
        // identity guard below and write its own result back over this one — and
        // since the effect's deps have not changed and the status reads `ready`,
        // nothing would ever ask again. Export would stay blocked for good.
        abort();
        setState((prev) => readyFromCache(prev, cached, key));
        return;
      }
      if (inFlightRef.current?.signature === signature) return;
      inFlightRef.current?.controller.abort();

      const controller = new AbortController();
      inFlightRef.current = { signature, controller };
      setState({
        status: "loading",
        comments: null,
        truncated: false,
        incomplete: NO_INCOMPLETE,
        key,
      });

      void (async () => {
        try {
          const result = await pmRepo.fetchProtocolComments(issueIds, {
            ...bounds,
            signal: controller.signal,
          });
          // Both guards: abort() does not un-resolve a settled promise, so only
          // the identity check catches an out-of-order pair.
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          cacheRef.current.set(key, { ...result, ids: new Set(issueIds), signature });
          inFlightRef.current = null;
          setState({ status: "ready", ...result, key });
        } catch (error) {
          if (controller.signal.aborted || inFlightRef.current?.controller !== controller) return;
          inFlightRef.current = null;
          // The step shows the failure and a retry; the console is where it is
          // diagnosed. Export stays blocked rather than printing a protocol that
          // claims every issue has no comments.
          console.error("Protocol comment fetch failed", error);
          setState({
            status: "error",
            comments: null,
            truncated: false,
            incomplete: NO_INCOMPLETE,
            key,
          });
        }
      })();
    },
    [abort]
  );

  return { state, load, abort };
}
